// @flow
import CoverageEditor from 'esm-web-pro/containers/pages/coverage/CoverageEditor';
import CoverageMapEditor from 'esm-web-pro/containers/pages/coverage/CoverageMapEditor';
import type { Resource, Map } from 'esm-web-pro/containers/map/types';
import L from 'leaflet';
import * as React from 'react';
import type { LatLng } from 'react-leaflet/lib/types';
import { withTheme } from 'styled-components';
import { FormCheckmark, FormClose } from 'grommet-icons';
import Async from 'esm-web-pro/components/Async';
import { Box, Image } from 'grommet';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import type { ApiClient } from 'speed-js-react';
import { connectAPI, withConfirmation, withRouter } from 'speed-js-react';
import { isEmpty } from 'lodash';
import { MapContext } from 'esm-web-pro/containers/map/context';
import { MAP_INITIAL_ZOOM } from 'esm-web-pro/containers/pages/network/NetworkTab';
import {
    buildingContact, insideCopperInstallation, insideOpticInstallation, verticalInstallation, scaffoldedFacade,
} from '../../map/iconImports';
import CoverageCreator from './CoverageCreator';
import { addressDisplayFields, getAddressMarkerAdditionalOptions } from './Util';

type Props = {
    intl: IntlShape,
    getParentLocation: (e: { layer: Object }) => Promise<Object>,
    getAddressContactDetails: (personId: number) => Promise<Object>,
    map: Map,
    setPosition: (center: LatLng, zoom: number) => {},
    setMapLoading: (mapLoading: boolean) => void,
    zoom: number,
    center: [number, number],
    theme: Object,
    history: { push: (location: string) => void },
    match: Object,
}

const geometryType = {
    polygon: 'Polygon',
    polyline: 'Polyline',
    point: 'Point',
    marker: 'Marker',
};

const defaultIconSize = [28, 41];

const messages = defineMessages({
    'addresses-coverage-btn': {
        id: 'maptab-coverage-btn',
        defaultMessage: 'Coverage',
    },
    'addresses-coverage-btn-plural': {
        id: 'maptab-coverage-btn-plural',
        defaultMessage: 'Coverage',
    },
    'addresses-address-btn': {
        id: 'maptab-address-btn',
        defaultMessage: 'Address',
    },
    'addresses-address-btn-plural': {
        id: 'maptab-address-btn-plural',
        defaultMessage: 'Addresses',
    },
    'addresses-vertical-addresses-title': {
        id: 'maptab-vertical-addresses-title',
        defaultMessage: 'Vertical Addresses',
    },
    'addresses-coverage-type-coverage': {
        id: 'maptab-coverage-type-coverage',
        defaultMessage: 'Coverage type',
    },
    'addresses-coverage-inherited-from-polygon': {
        id: 'maptab-coverage-inherited-from-polygon',
        defaultMessage: 'Inherited from polygon',
    },
    'addresses-coverage-inherited-from-map-boundaries': {
        id: 'maptab-coverage-inherited-from-map-boundaries',
        defaultMessage: 'Inherited from map boundaries',
    },
    'addresses-coverage-specific-to-address': {
        id: 'maptab-coverage-specific-to-address',
        defaultMessage: 'Specific to address',
    },
    'addresses-coverage-no-coverage': {
        id: 'maptab-coverage-no-coverage',
        defaultMessage: 'No coverage',
    },
    'addresses-coverage-type-full': {
        id: 'addresses-coverage-type-full',
        defaultMessage: 'Full',
    },
    'addresses-coverage-type-partial': {
        id: 'addresses-coverage-type-partial',
        defaultMessage: 'Partial',
    },
    'addresses-coverage-type-outside-operational-region': {
        id: 'addresses-coverage-type-outside-operational-region',
        defaultMessage: 'Outside operational region',
    },
    'addresses-empty-title-label': {
        id: 'maptab-empty-title-label',
        defaultMessage: 'N/A',
    },
    'addresses-type-horizontal': {
        id: 'maptab-address-type-horizontal',
        defaultMessage: 'Horizontal',
    },
    'addresses-type-vertical': {
        id: 'maptab-address-type-vertical',
        defaultMessage: 'Vertical',
    },
    'attribute-no': {
        id: 'attributes-no',
        defaultMessage: 'No',
    },
    'attribute-yes': {
        id: 'attributes-yes',
        defaultMessage: 'Yes',
    },
    'maptab-edit-address-title': {
        id: 'maptab-edit-address-title',
        defaultMessage: 'Edit',
    },

});

const COLOR = {
    green: '#0074d9',
    darkerGreen: '#809c17',
    red: '#d02120',
    blue: '#0000FF',
    grey: '#606060',
};

export const ADDRESS_ATTRIBUTE_ICONS = {
    lfc: buildingContact,
    voi: insideOpticInstallation,
    sf: scaffoldedFacade,
    ppv: verticalInstallation,
    vmi: insideCopperInstallation,
};

@connectAPI((apiClient: ApiClient) => ({
    getParentLocation: {
        send: ({ layer }) => (
            apiClient.resources.location.list({
                polygon__covers: JSON.stringify(layer.toGeoJSON().geometry),
                ordering: '-level',
                limit: 1,
            }).then((response) => ({
                parent: response.results[0] ? response.results[0].id : null,
            }))
        ),
    },
    getAddressContactDetails: {
        send: (personId) => apiClient.resources.entity.details.list(personId),
    },
}))
@withConfirmation
class CoverageTab extends React.Component<Props> {
    resources: Array<Resource>;

    ref: Object;

    constructor(props) {
        super(props);
        this.ref = React.createRef();
    }

    getResources = (map) => {
        const resources = [];

        if (map) {
            const { getParentLocation } = this.props;

            const addressResource = {
                name: ['address.map', 'addresses'],
                sidePaneResource: 'address',
                key: 'not-changed',
                arguments: [map.uuid],
                label: messages['addresses-address-btn'],
                labelPlural: messages['addresses-address-btn-plural'],
                type: 'point',
                geometryField: 'geom',
                query: this.getAddressQuery(map),
                allowNew: false,
                allowEdit: false,
                allowDelete: false,
                additionalInfo: this.getAddressAdditionalInfo,
                minZoom: !map.is_draft ? 18 : undefined,
                additionalOptions: (feature) => getAddressMarkerAdditionalOptions(feature.properties),
                tooltipContent: this.getTooltipContent,
                navigateTo: this.navigateToAddress,
                setFocusedLayerIconOptions: this.setFocusedLayerIconOptions,
            };

            resources.push(
                {
                    name: ['address.map', 'coverages'],
                    sidePaneResource: 'coverage',
                    arguments: [map.uuid],
                    label: messages['addresses-coverage-btn'],
                    labelPlural: messages['addresses-coverage-btn-plural'],
                    type: 'polygon',
                    geometryField: 'polygon',
                    query: {
                        geojson: true,
                        ordering: 'parent,name',
                        nonested: 1,
                    },
                    allowNew: map.is_draft,
                    allowEdit: map.is_draft,
                    allowDelete: map.is_draft,
                    getInitial: getParentLocation,
                    additionalOptions: (feature) => this.getPolygonAdditionalOptions(map, feature),
                    key: 'map-coverages',
                    editor: CoverageEditor,
                    navigateTo: this.navigateTo,
                },
            );
            resources.push(addressResource);
        }

        return resources;
    };

    setFocusedLayerIconOptions = (focusedLayer, focusLayer, options) => {
        const { incrementSize } = options;
        if (focusedLayer && focusedLayer !== focusLayer && focusedLayer.feature.geometry.type === geometryType.point) {
            const { icon } = focusedLayer.options;
            if (icon.options) {
                icon.options.iconSize = [icon.options.iconSize[0]
                - incrementSize, icon.options.iconSize[1] - incrementSize];
                icon.options.iconAnchor = [icon.options.iconSize[0] / 2, icon.options.iconSize[1]];
                focusedLayer.setIcon(icon);
            }
        }
        if (focusLayer.feature.geometry.type === geometryType.point) {
            const { icon } = focusLayer.options;
            if (icon.options.iconSize[0] <= defaultIconSize[0] && icon.options.iconSize[1] <= defaultIconSize[1]) {
                icon.options.iconSize = [icon.options.iconSize[0]
                + incrementSize, icon.options.iconSize[1] + incrementSize];
                // icon.options.iconAnchor = [icon.options.iconSize[0] / 2, icon.options.iconSize[1] + 4];
                focusLayer.setIcon(icon);
            }
        }
        return focusLayer;
    };

    getPolygonAdditionalOptions = (map: Object = null, feature: Object) => {
        const options: Object = {};

        const coverage = feature.properties;
        if (!coverage.valid_from && coverage.valid_to) {
            options.color = COLOR.red;
            if (map) {
                const versionCreateDate = new Date(map.version_create_date);
                const activeMapValidFrom = new Date(feature.properties.modified);
                if (versionCreateDate < activeMapValidFrom) {
                    options.fillOpacity = 0.7;
                    options.className = 'deleted-coverage';
                }
            }
        } else if (!coverage.valid_from) {
            options.color = COLOR.darkerGreen;
            options.fillOpacity = 0.5;
            options.fillColor = COLOR.darkerGreen;
            options.className = 'draft-coverage';
        } else {
            options.fillOpacity = 0.2;
            options.color = COLOR.green;
            options.fillColor = null;
            options.className = 'active-coverage';
        }
        return options;
    };


    getAddressQuery = (map) => () => {
        const query: Object = {
            geojson: true,
            limit: 1000,
        };

        if (map.is_draft && this.ref.current && this.ref.current.map.current) {
            const { leafletElement } = this.ref.current.map.current;
            const zoomLevel = leafletElement.getZoom();

            if (zoomLevel < 18) {
                query.changed = 1;
            }
        }

        return query;
    };

    getAddressAdditionalInfo = (address) => {
        const { intl } = this.props;

        if (address.attributes) {
            // eslint-disable-next-line array-callback-return
            return Object.values(address.attributes).map((attribute: Object) => {
                const { getAddressContactDetails, theme } = this.props;
                const { type: { value_type: valType } } = attribute;
                if (!attribute.value) {
                    return null;
                }

                let value;
                if (valType === 'choice') {
                    // TODO: Use other way of determine the type of choice
                    if (attribute.value === 'Yes' || attribute.value === 'Има') {
                        value = <FormCheckmark color={theme.global.colors.brand} />;
                    } else if (attribute.value === 'No' || attribute.value === 'Няма') {
                        value = <FormClose />;
                        // eslint-disable-next-line prefer-destructuring
                    } else { value = attribute.value; }
                    // $FlowFixMe
                } else { value = valType === 'related' ? attribute.value.name : attribute.value; }
                return (
                    <Box direction="row" align="center" key={attribute.type.code}>
                        <Box pad="xsmall">
                            <Image
                                src={ADDRESS_ATTRIBUTE_ICONS[attribute.type.code]}
                                style={{ width: '15px', height: '15px' }}
                            />
                        </Box>
                        <Box>
                            <Box style={{ alignItems: 'center' }} direction="row">
                                {`${attribute.type.translations[intl.locale].name}: `}
                                {value}
                            </Box>
                            {(attribute.type.code === 'lfc') && ( // lfc is code for contact details
                                <Async
                                    // $FlowFixMe
                                    promise={getAddressContactDetails(attribute.value.uuid)}
                                    then={(response) => response.results.map((contactDetail) => (contactDetail.detail
                                        && (
                                            <Box key={contactDetail.detail}>
                                                {`${contactDetail.type}: ${contactDetail.detail}`}
                                            </Box>
                                        )
                                    ))}
                                />
                            )}
                        </Box>
                    </Box>
                );
            });
        }

        return null;
    };

    getTooltipContent = ({ feature }) => {
        let content = '';
        const { coverage } = feature.properties;
        const { formatMessage } = this.props.intl;

        const transformed = addressDisplayFields.map((f) => feature.properties[f]).filter((f) => !!f).join(' ');
        content += `${formatMessage(messages['addresses-address-btn'])}: ${transformed} <br/>`;

        content += `${formatMessage(messages['addresses-coverage-btn'])}: `;
        if (isEmpty(coverage)) {
            content += `<b style="color:${COLOR.grey}">
                         ${formatMessage(messages['addresses-coverage-type-outside-operational-region'])}
                        </b> <br/>`;
        } else if (coverage && coverage.type === 'full') {
            content += `<b style="color:${COLOR.green}">
                         ${formatMessage(messages['addresses-coverage-type-full'])}
                        </b> <br/>`;
        } else {
            content += `<b style="color:${COLOR.blue}">
                        ${formatMessage(messages['addresses-coverage-type-partial'])}
                        </b> <br/>`;
        }

        content += `${formatMessage(messages['addresses-coverage-type-coverage'])}: `;
        if (coverage && coverage.origin === 'specific') {
            content += `<b color="">${
                formatMessage(messages['addresses-coverage-specific-to-address'])}</b>`;
        } else if (coverage && coverage.origin === 'inherited_map') {
            content += `<b color="">${
                formatMessage(messages['addresses-coverage-inherited-from-map-boundaries'])}</b>`;
        } else if (coverage && coverage.origin === 'inherited_polygon') {
            content += `<b color="">${
                formatMessage(messages['addresses-coverage-inherited-from-polygon'])}</b>`;
        } else if (isEmpty(coverage)) {
            content += `<b color="${COLOR.grey}">
            ${formatMessage(messages['addresses-coverage-type-outside-operational-region'])}
            </b>`;
        }

        return content;
    };

    navigateToAddress = (layer, result) => {
        const { history, match } = this.props;
        history.push(`${match.url}/layer/${result.resource.sidePaneResource}/${layer.feature.id}/`);
    };

    navigateTo = (layer, result) => {
        const { history, match } = this.props;
        history.push(`${match.url}/layer/${result.resource.sidePaneResource}/${layer.feature.properties.identity}/`);
    };

    render() {
        const {
            map, setPosition, center, zoom, setMapLoading,
        } = this.props;
        return (
            <>
                <Box height="100%">
                    <MapContext.Provider value={map}>
                        <CoverageMapEditor
                            forwardedRef={this.ref}
                            zoom={zoom || MAP_INITIAL_ZOOM}
                            center={
                                center || L.polygon(
                                    map.bounds.coordinates[0].map((c) => [c[1], c[0]]),
                                ).getBounds().getCenter()
                            }
                            setPosition={setPosition}
                            setMapLoading={setMapLoading}
                            resources={this.getResources(map)}
                            bounds={map.bounds.coordinates[0]}
                            currentMap={map}
                            allowCreate={map.is_draft}
                            creator={CoverageCreator}
                        />
                    </MapContext.Provider>
                </Box>
            </>
        );
    }
}

export default withRouter(withTheme(injectIntl(CoverageTab)));
